import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { Recalls } from '@principle-theorem/principle-core';
import {
  IBrand,
  IRecallSettings,
  ITreatmentConfiguration,
  RecallTrack,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  isSameRef,
  resolveDocRefs,
  WithRef,
} from '@principle-theorem/shared';
import { intersectionWith } from 'lodash';
import { of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'pr-recall-settings-summary',
  templateUrl: './recall-settings-summary.component.html',
  styleUrls: ['./recall-settings-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, NgCommonModule, MatTooltipModule],
})
export class RecallSettingsSummaryComponent {
  treatments: Signal<WithRef<ITreatmentConfiguration>[]>;
  settings = input<IRecallSettings>();
  brand = input<WithRef<IBrand>>();
  otherTrackTreatmentRefs = input<DocumentReference<ITreatmentConfiguration>[]>(
    []
  );
  otherTrack = input<RecallTrack | undefined>(undefined);

  localRefs: Signal<DocumentReference<ITreatmentConfiguration>[]>;
  overlapTreatments: Signal<DocumentReference<ITreatmentConfiguration>[]>;
  otherTrackLabel: Signal<string>;
  showOverlapChip: Signal<boolean>;
  overlapTooltip: Signal<string>;

  constructor() {
    const settings$ = toObservable(this.settings);
    const treatments$ = settings$.pipe(
      switchMap((settings) => {
        if (!settings || !settings.treatmentRefs.length) {
          return of([]);
        }
        return resolveDocRefs(settings.treatmentRefs);
      })
    );
    this.treatments = toSignal(treatments$, { initialValue: [] });

    this.localRefs = toSignal(
      settings$.pipe(map((settings) => settings?.treatmentRefs ?? [])),
      {
        initialValue: [] as DocumentReference<ITreatmentConfiguration>[],
      }
    );

    this.overlapTreatments = computed(() =>
      intersectionWith(
        this.localRefs(),
        this.otherTrackTreatmentRefs(),
        isSameRef
      )
    );

    this.otherTrackLabel = computed(() => {
      const track = this.otherTrack();
      return track !== undefined ? Recalls.trackLabel(this.brand(), track) : '';
    });

    this.showOverlapChip = computed(
      () =>
        this.otherTrack() !== undefined && this.overlapTreatments().length > 0
    );

    this.overlapTooltip = computed(
      () =>
        `These treatments are also part of the ${this.otherTrackLabel()}. ` +
        `Appointments using them will update both recall tracks.`
    );
  }
}
